import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { WS } from './assets';
import { createPortal } from 'react-dom';
import { TR, reasonLabel, padPos, maskPublicName } from './copy';

const NAV = [
  { id: 'home', label: TR.nav.home, dp: 'gf', icon: WS.nav.home },
  { id: 'missions', label: TR.nav.missions, dp: 'gf_missions', icon: WS.nav.missions },
  { id: 'levels', label: TR.nav.levels, dp: 'gf_levels', icon: WS.nav.levels },
  { id: 'board', label: TR.nav.board, dp: 'gf_board', icon: WS.nav.board },
  { id: 'store', label: TR.nav.store, dp: 'gf_store', icon: WS.nav.store },
  { id: 'activity', label: TR.nav.activity, dp: 'gf_activity', icon: WS.nav.inbox },
];

const MISSION_TABS = [
  { id: 'overview', label: TR.missionTabs.overview },
  { id: 'available', label: TR.missionTabs.available },
  { id: 'locked', label: TR.missionTabs.locked },
  { id: 'completed', label: TR.missionTabs.completed },
  { id: 'missed', label: TR.missionTabs.missed },
];

function fmt(n) {
  return Number(n || 0).toLocaleString('tr-TR');
}

function missionBucket(m) {
  if (m.category === 'missed' || m.status === 'missed' || m.status === 'expired') return 'missed';
  if (m.is_completed || m.category === 'completed' || m.status === 'completed' || m.status === 'claimed') return 'completed';
  if (m.is_locked || m.category === 'locked' || m.status === 'locked') return 'locked';
  const prog = missionProgress(m);
  if (prog > 0 || m.status === 'in_progress') return 'available';
  if (m.is_requires_optin && !m.is_opted_in) return 'available';
  return m.category || 'available';
}

function missionProgress(m) {
  const p = Number(m?.progress);
  if (Number.isFinite(p) && p >= 0) return Math.max(0, Math.min(100, Math.round(p)));
  const tasks = Array.isArray(m?.tasks) ? m.tasks : [];
  if (!tasks.length) {
    if (m?.is_completed || m?.prize_claimed_date_ts || missionBucket(m) === 'completed') return 100;
    return 0;
  }
  const ratios = tasks.map((t) => {
    const target = Math.max(1, Number(t.execution_count_expected ?? t.target_value) || 1);
    const cur = Number(t.execution_count_actual ?? t.current ?? 0);
    return Math.min(1, cur / target);
  });
  return Math.round((ratios.reduce((a, b) => a + b, 0) / ratios.length) * 100);
}

function statusChip(m) {
  const bucket = missionBucket(m);
  if (bucket === 'locked') return TR.missionStatus.locked;
  if (bucket === 'missed') return TR.missionStatus.missed;
  if (m.prize_claimed_date_ts || m.status === 'claimed') return TR.missionStatus.claimed;
  if (m.is_completed && m.requires_prize_claim && !m.prize_claimed_date_ts) return TR.missionStatus.claimable;
  if (m.is_completed || bucket === 'completed') return TR.missionStatus.completed;
  if (m.is_requires_optin && !m.is_opted_in) return TR.missionStatus.requires_optin;
  if (missionProgress(m) > 0 || m.status === 'in_progress') return TR.missionStatus.in_progress;
  return TR.missionStatus.available;
}

function isClaimable(m) {
  return Boolean(m.is_completed && m.requires_prize_claim && !m.prize_claimed_date_ts);
}

function Progress({ value, animate }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className="ws-progress" style={{ backgroundImage: `url(${WS.progress})` }}>
      <i className={animate ? 'is-anim' : ''} style={{ width: `${pct}%`, backgroundImage: `url(${WS.progressFill})` }} />
    </div>
  );
}

function Empty({ src, text }) {
  return (
    <div className="ws-empty">
      <img src={src || WS.empty.default} alt="" />
      <p>{text}</p>
    </div>
  );
}

function Toast({ text, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2200);
    return () => clearTimeout(t);
  }, [onDone]);
  return <div className="ws-toast" role="status">{text}</div>;
}

function Popup({ children, onClose, wide }) {
  return (
    <div className="ws-gf__modal" onClick={onClose}>
      <div className={`ws-popup ws-popup--in${wide ? ' ws-popup--wide' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="ws-popup__grid">
          <i style={{ backgroundImage: `url(${WS.popup.tl})` }} />
          <i style={{ backgroundImage: `url(${WS.popup.t})` }} />
          <i style={{ backgroundImage: `url(${WS.popup.tr})` }} />
          <i style={{ backgroundImage: `url(${WS.popup.l})` }} />
          <div className="ws-popup__body" style={{ backgroundImage: `url(${WS.popup.c})` }}>
            <button type="button" className="ws-popup__x" onClick={onClose} aria-label={TR.header.close}>
              <img src={WS.chrome.close} alt="" />
            </button>
            {children}
          </div>
          <i style={{ backgroundImage: `url(${WS.popup.r})` }} />
          <i style={{ backgroundImage: `url(${WS.popup.bl})` }} />
          <i style={{ backgroundImage: `url(${WS.popup.b})` }} />
          <i style={{ backgroundImage: `url(${WS.popup.br})` }} />
        </div>
      </div>
    </div>
  );
}

function LockMark() {
  return <span className="ws-lock-mark" aria-hidden="true" />;
}

function normalizeBoardType(boardType) {
  if (boardType === '1' || boardType === 1) return 'daily';
  if (boardType === '3' || boardType === 3) return 'monthly';
  if (boardType === '2' || boardType === 2) return 'weekly';
  if (['daily', 'weekly', 'monthly'].includes(boardType)) return boardType;
  return 'weekly';
}

function prevToggleLabel(lbType, previous) {
  if (previous) {
    if (lbType === 'daily') return TR.board.currDaily;
    if (lbType === 'monthly') return TR.board.currMonthly;
    return TR.board.currWeekly;
  }
  if (lbType === 'daily') return TR.board.prevDaily;
  if (lbType === 'monthly') return TR.board.prevMonthly;
  return TR.board.prevWeekly;
}

export default function WowSmartWidget({
  open,
  standalone = false,
  hideMain = false,
  section = 'home',
  missionCategory = 'overview',
  boardType = 'weekly',
  focusId = null,
  onClose,
  onDeepLink,
  wsApi,
  guest = false,
  onRegister,
  openNickname = false,
}) {
  const [profile, setProfile] = useState(null);
  const [missions, setMissions] = useState([]);
  const [levels, setLevels] = useState([]);
  const [board, setBoard] = useState(null);
  const [store, setStore] = useState([]);
  const [cats, setCats] = useState([]);
  const [storeCat, setStoreCat] = useState('all');
  const [storeHist, setStoreHist] = useState([]);
  const [storeTab, setStoreTab] = useState('items');
  const [storeQ, setStoreQ] = useState('');
  const [pointsHist, setPointsHist] = useState([]);
  const [activity, setActivity] = useState([]);
  const [actFilter, setActFilter] = useState('all');
  const [tab, setTab] = useState(section);
  const [mTab, setMTab] = useState(missionCategory || 'overview');
  const [lbType, setLbType] = useState(normalizeBoardType(boardType));
  const [lbPrev, setLbPrev] = useState(false);
  const [lbSection, setLbSection] = useState('leaders');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nickOpen, setNickOpen] = useState(false);
  const [nick, setNick] = useState('');
  const [detail, setDetail] = useState(null);
  const [levelDetail, setLevelDetail] = useState(null);
  const [buyItem, setBuyItem] = useState(null);
  const [toast, setToast] = useState('');
  const navRef = useRef(null);
  const focusApplied = useRef('');

  useEffect(() => { setTab(section); }, [section]);
  useEffect(() => { setMTab(missionCategory || 'overview'); }, [missionCategory]);
  useEffect(() => { if (openNickname) setNickOpen(true); }, [openNickname]);
  useEffect(() => { setLbType(normalizeBoardType(boardType)); }, [boardType]);
  useEffect(() => { if (!open) focusApplied.current = ''; }, [open]);

  const load = useCallback(async () => {
    if (!open || !wsApi) return;
    setErr('');
    setLoading(true);
    try {
      if (guest) {
        const [lv, ms, st, bd] = await Promise.all([
          wsApi.getLevels(),
          wsApi.getMissions(),
          wsApi.getStoreItems(),
          wsApi.getLeaderBoard(lbType, lbPrev),
        ]);
        setLevels(lv);
        setMissions((ms || []).map((m) => ({ ...m, category: 'available', tasks: m.tasks || [] })));
        setStore(st);
        setBoard(bd);
        setProfile({
          public_username: 'Misafir',
          nickname: 'Misafir',
          ach_points_balance: 0,
          ach_points_ever: 0,
          ach_gems_balance: 0,
          ach_diamonds_balance: 0,
          level: lv[0] || null,
          next_level: lv[1] || null,
          level_progress_pct: 0,
        });
        return;
      }
      const [p, ms, lv, bd, st, ct, act, ph, sh] = await Promise.all([
        wsApi.getUserProfile(),
        wsApi.getMissions(),
        wsApi.getLevels(),
        wsApi.getLeaderBoard(lbType, lbPrev),
        wsApi.getStoreItems(),
        wsApi.getStoreCategories(),
        wsApi.getActivityLog ? wsApi.getActivityLog() : Promise.resolve([]),
        wsApi.getPointsHistory ? wsApi.getPointsHistory() : Promise.resolve([]),
        wsApi.getStorePurchasedItems ? wsApi.getStorePurchasedItems() : (wsApi.getStoreHistory ? wsApi.getStoreHistory() : Promise.resolve([])),
      ]);
      setProfile(p);
      setMissions(ms || []);
      setLevels(lv || []);
      setBoard(bd);
      setStore(st || []);
      setCats(ct || []);
      setActivity(act || []);
      setPointsHist(ph || []);
      setStoreHist(sh || []);

      const key = `${section}:${focusId}:${hideMain}:${openNickname}`;
      if (focusId && focusApplied.current !== key) {
        focusApplied.current = key;
        if (section === 'missions' || (hideMain && section !== 'levels' && section !== 'store')) {
          const found = (ms || []).find((m) => Number(m.id) === Number(focusId));
          if (found) setDetail(found);
        }
        if (section === 'levels') {
          const found = (lv || []).find((l) => Number(l.id) === Number(focusId));
          if (found) setLevelDetail(found);
        }
        if (section === 'store') {
          const found = (st || []).find((i) => Number(i.id) === Number(focusId));
          if (found) setBuyItem(found);
        }
      }
    } catch (e) {
      setErr(e.message || TR.common.error);
    } finally {
      setLoading(false);
    }
  }, [open, wsApi, guest, lbType, lbPrev, focusId, hideMain, section, openNickname]);

  useEffect(() => { load(); }, [load]);

  const filteredMissions = useMemo(() => {
    if (mTab === 'overview') {
      const avail = missions.filter((m) => missionBucket(m) === 'available');
      const locked = missions.filter((m) => missionBucket(m) === 'locked').slice(0, 2);
      const claimable = missions.filter((m) => isClaimable(m));
      const seen = new Set();
      const out = [];
      for (const m of [...claimable, ...avail, ...locked]) {
        if (seen.has(m.id)) continue;
        seen.add(m.id);
        out.push(m);
      }
      return out.length ? out : missions;
    }
    return missions.filter((m) => missionBucket(m) === mTab);
  }, [missions, mTab]);

  const homeMissions = useMemo(() => {
    const claimable = missions.filter((m) => isClaimable(m));
    const progress = missions.filter((m) => !isClaimable(m) && missionBucket(m) === 'available' && missionProgress(m) > 0);
    const avail = missions.filter((m) => missionBucket(m) === 'available' && missionProgress(m) === 0);
    const seen = new Set();
    const out = [];
    for (const m of [...claimable, ...progress, ...avail]) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      out.push(m);
      if (out.length >= 4) break;
    }
    return out;
  }, [missions]);

  const homeStats = useMemo(() => {
    const claimable = missions.filter((m) => isClaimable(m)).length;
    const inProgress = missions.filter((m) => missionBucket(m) === 'available' && missionProgress(m) > 0 && !isClaimable(m)).length;
    return { claimable, inProgress };
  }, [missions]);

  const homeBoard = useMemo(() => (board?.users || board?.items || []).slice(0, 3), [board]);

  const homeStore = useMemo(() => store.slice(0, 3), [store]);

  const homeShortcuts = useMemo(() => ([
    { id: 'missions', label: TR.home.shortcutMissions, icon: WS.home.missions, box: WS.boxes.tall, nav: 'missions' },
    { id: 'levels', label: TR.home.shortcutLevels, icon: WS.home.levels, box: WS.boxes.big, nav: 'levels' },
    { id: 'board', label: TR.home.shortcutBoard, icon: WS.nav.board, box: WS.boxes.card, nav: 'board' },
    { id: 'store', label: TR.home.shortcutStore, icon: WS.nav.store, box: WS.boxes.storeHome, nav: 'store' },
  ]), []);

  const visibleLevels = useMemo(() => {
    const ever = Number(profile?.ach_points_ever || 0);
    return (levels || []).filter((lv) => {
      const vis = Number(lv.visibility_points || 0);
      if (vis <= 0) return true;
      return ever >= vis || Number(profile?.level?.required_points || 0) >= Number(lv.required_points || 0);
    });
  }, [levels, profile]);

  const filteredStore = useMemo(() => {
    let list = storeCat === 'all'
      ? [...store]
      : store.filter((i) => (i.category_ids || []).some((cid) => Number(cid) === Number(storeCat)));
    const q = storeQ.trim().toLocaleLowerCase('tr-TR');
    if (q) list = list.filter((i) => String(i.name || '').toLocaleLowerCase('tr-TR').includes(q));
    list.sort((a, b) => {
      const aOk = a.can_buy !== false ? 1 : 0;
      const bOk = b.can_buy !== false ? 1 : 0;
      if (aOk !== bOk) return bOk - aOk;
      return Number(a.price ?? a.points_cost) - Number(b.price ?? b.points_cost);
    });
    return list;
  }, [store, storeCat, storeQ]);

  const filteredPoints = useMemo(() => {
    const now = Date.now();
    const day = 86400000;
    return (pointsHist || []).filter((a) => {
      if (actFilter === 'all') return true;
      const t = a.created_at ? new Date(a.created_at).getTime() : 0;
      if (actFilter === 'today') return now - t < day;
      if (actFilter === 'week') return now - t < day * 7;
      return true;
    });
  }, [pointsHist, actFilter]);

  if (!open) return null;

  const act = async (key, fn, { successToast } = {}) => {
    setBusy(key);
    setErr('');
    try {
      await fn();
      await load();
      if (successToast) setToast(successToast);
    } catch (e) {
      setErr(e.message || TR.common.error);
    } finally {
      setBusy(null);
    }
  };

  const goTab = (n) => {
    setTab(n.id);
    setDetail(null);
    setLevelDetail(null);
    setBuyItem(null);
    onDeepLink?.(`dp:${n.dp}`);
  };

  const scrollNav = (dir) => {
    navRef.current?.scrollBy({ left: dir * 160, behavior: 'smooth' });
  };

  const nextNeed = profile?.next_level
    ? Math.max(0, Number(profile.next_level.required_points) - Number(profile.ach_points_ever || 0))
    : 0;

  const lbBg = (row) => {
    if (row.is_me) return WS.lb.me;
    if (row.position === 1) return WS.lb[1];
    if (row.position === 2) return WS.lb[2];
    if (row.position === 3) return WS.lb[3];
    return WS.lb.other;
  };

  const hideChrome = standalone;
  const showClaimOnCard = (m) => isClaimable(m) && !guest;
  const me = board?.me;
  const boardRows = board?.users || board?.items || [];
  const meInTop = boardRows.some((r) => r.is_me);
  const showStickyMe = !guest && me && (!meInTop || me.position > 20 || me.position === -1);

  const displayName = (row) => maskPublicName(row?.public_username || row?.nickname || 'Oyuncu', { isMe: Boolean(row?.is_me) });

  const renderBoardRow = (row) => {
    const name = displayName(row);
    return (
      <li
        key={`${row._home ? 'h-' : ''}${row.user_id}-${row.position}`}
        className={[
          'ws-board__row',
          row.is_me ? 'is-me' : '',
          row.position === 1 ? 'place-1' : '',
          row.position === 2 ? 'place-2' : '',
          row.position === 3 ? 'place-3' : '',
        ].filter(Boolean).join(' ')}
        style={{ backgroundImage: `url(${lbBg(row)})` }}
      >
        <span className="ws-board__pos">{padPos(row.position)}</span>
        <span className="ws-board__ava">
          {row.avatar_url ? (
            <img src={row.avatar_url} alt="" />
          ) : (
            <img src={WS.chrome.user} alt="" className="ws-board__ava-fallback" />
          )}
        </span>
        <span className="ws-board__name">
          {name}
          {row.is_me ? <em> · {TR.board.you}</em> : null}
        </span>
        <span className="ws-board__pts">
          <img src={WS.home.points} alt="" />
          {fmt(row.points)}
        </span>
      </li>
    );
  };

  const ui = (
    <div className={`ws-gf${standalone ? ' ws-gf--standalone' : ''}${hideMain ? ' ws-gf--hide-main' : ''}`} role="dialog" aria-label={TR.brand}>
      <div className="ws-gf__scrim" onClick={onClose} />
      <div
        className="ws-gf__panel"
        style={{ borderImageSource: `url(${standalone ? WS.chrome.standaloneBorder : WS.chrome.border})` }}
      >
        {!hideChrome && (
          <header className="ws-gf__header">
            <button type="button" className="ws-gf__avatar" onClick={() => !guest && setNickOpen(true)} aria-label={TR.header.changeNick}>
              <img src={profile?.avatar_url || WS.chrome.user} alt="" className="ws-gf__avatar-img" />
            </button>
            <div className="ws-gf__who">
              <button type="button" className="ws-gf__nick-row" onClick={() => !guest && setNickOpen(true)} disabled={guest}>
                <span className="ws-gf__nick">{profile?.public_username || profile?.nickname || '—'}</span>
                {!guest ? <img src={WS.nav.nickEdit} alt="" className="ws-gf__nick-edit" /> : null}
              </button>
              <div className="ws-gf__lvl-chip">
                <img src={WS.home.levels} alt="" />
                <span>{profile?.level?.name || 'Çaylak'}</span>
              </div>
            </div>

            <div className="ws-gf__wallets">
              <div className="ws-gf__score" style={{ backgroundImage: `url(${WS.chrome.headerScore})` }} title={TR.header.pointsTip}>
                <img src={WS.home.points} alt="" />
                <strong>{fmt(profile?.ach_points_balance)}</strong>
              </div>
              <div className="ws-gf__mini-wallet" title={TR.header.gemsTip}>
                <img src={WS.home.gems} alt="" />
                <span>{fmt(profile?.ach_gems_balance)}</span>
              </div>
              <div className="ws-gf__mini-wallet" title={TR.header.diamondsTip}>
                <img src={WS.home.diamonds} alt="" />
                <span>{fmt(profile?.ach_diamonds_balance)}</span>
              </div>
            </div>

            <div className="ws-gf__xp">
              <Progress value={profile?.level_progress_pct || 0} animate />
              <small>
                {profile?.next_level
                  ? TR.header.nextLevel(profile.next_level.name, fmt(nextNeed))
                  : TR.header.topLevel}
              </small>
            </div>

            <button
              type="button"
              className={`ws-gf__inbox${tab === 'activity' ? ' is-on' : ''}`}
              onClick={() => goTab(NAV.find((n) => n.id === 'activity'))}
              aria-label={TR.header.inbox}
            >
              <img src={WS.nav.inbox} alt="" />
            </button>
            <button type="button" className="ws-gf__close" onClick={onClose} aria-label={TR.header.close}>
              <img src={WS.chrome.close} alt="" />
            </button>
          </header>
        )}

        {!hideChrome && (
          <div className="ws-gf__nav-wrap">
            <button type="button" className="ws-gf__nav-arrow" onClick={() => scrollNav(-1)} aria-label="Önceki">
              <img src={WS.chrome.arrowLeft} alt="" />
            </button>
            <nav className="ws-gf__nav" aria-label={`${TR.brand} menü`} ref={navRef}>
              {NAV.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={`ws-gf__nav-btn${tab === n.id ? ' is-active' : ''}`}
                  style={{ backgroundImage: `url(${tab === n.id ? WS.chrome.navBgActive : WS.chrome.navBg})` }}
                  onClick={() => goTab(n)}
                >
                  <img src={n.icon} alt="" />
                  <span>{n.label}</span>
                </button>
              ))}
            </nav>
            <button type="button" className="ws-gf__nav-arrow" onClick={() => scrollNav(1)} aria-label="Sonraki">
              <img src={WS.chrome.arrowRight} alt="" />
            </button>
          </div>
        )}

        <div
          className="ws-gf__main"
          style={{ backgroundImage: `url(${standalone ? WS.chrome.standaloneBg : WS.chrome.contentBg})` }}
        >
          {err ? (
            <div className="ws-gf__err">
              {err}
              <button type="button" className="ws-link" onClick={load}>{TR.common.retry}</button>
            </div>
          ) : null}
          {guest ? (
            <div className="ws-gf__guest">
              <p>{TR.guest.text}</p>
              <button type="button" onClick={onRegister}>{TR.guest.cta}</button>
            </div>
          ) : null}

          <div className="ws-gf__body">
            {loading && !missions.length && !store.length ? (
              <div className="ws-loading">{TR.common.loading}</div>
            ) : null}

            {tab === 'home' && (
              <div className="ws-home">
                <div className="ws-home__hero" style={{ backgroundImage: `url(${WS.boxes.bonus})` }}>
                  <div>
                    <p className="ws-home__brand">{TR.brand}</p>
                    <h2>{TR.home.welcome(profile?.public_username || profile?.nickname || 'Oyuncu')}</h2>
                    <p className="ws-home__sub">{TR.brandSub}</p>
                  </div>
                  <div className="ws-home__wallet-stack">
                    <div className="ws-home__points">
                      <img src={WS.home.points} alt="" />
                      <div>
                        <strong>{fmt(profile?.ach_points_balance)}</strong>
                        <span>{TR.home.pointsLabel}</span>
                      </div>
                    </div>
                    <div className="ws-home__ever">
                      <img src={WS.home.pointsTotal} alt="" />
                      <div>
                        <strong>{fmt(profile?.ach_points_ever)}</strong>
                        <span>{TR.home.everLabel}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {(homeStats.claimable > 0 || homeStats.inProgress > 0) ? (
                  <div className="ws-home__stats">
                    {homeStats.claimable > 0 ? (
                      <button type="button" className="ws-home__stat ws-home__stat--claim" onClick={() => goTab(NAV.find((n) => n.id === 'missions'))}>
                        <img src={WS.chrome.check} alt="" />
                        {TR.home.claimable(homeStats.claimable)}
                      </button>
                    ) : null}
                    {homeStats.inProgress > 0 ? (
                      <button type="button" className="ws-home__stat" onClick={() => goTab(NAV.find((n) => n.id === 'missions'))}>
                        <img src={WS.home.missions} alt="" />
                        {TR.home.inProgress(homeStats.inProgress)}
                      </button>
                    ) : null}
                  </div>
                ) : null}

                <div className="ws-home__level" style={{ backgroundImage: `url(${WS.boxes.card})` }} onClick={() => goTab(NAV.find((n) => n.id === 'levels'))}>
                  <div className="ws-home__level-ico" style={{ backgroundImage: `url(${WS.chrome.navBgActive})` }}>
                    <img src={WS.home.levels} alt="" />
                  </div>
                  <div className="ws-home__level-copy">
                    <em>{TR.home.levelTitle}</em>
                    <strong>{profile?.level?.name || 'Çaylak'}</strong>
                    <Progress value={profile?.level_progress_pct || 0} animate />
                    <small>
                      {profile?.next_level
                        ? TR.header.nextLevel(profile.next_level.name, fmt(nextNeed))
                        : TR.header.topLevel}
                    </small>
                  </div>
                  <img src={WS.chrome.dashArrowOrange} alt="" className="ws-home__arrow" />
                </div>

                <div className="ws-home__section">
                  <div className="ws-home__section-head">
                    <h3>{TR.home.shortcutsTitle}</h3>
                  </div>
                  <ul className="ws-home__tiles">
                    {homeShortcuts.map((s) => (
                      <li key={s.id} style={{ backgroundImage: `url(${s.box})` }}>
                        <button type="button" onClick={() => goTab(NAV.find((n) => n.id === s.nav))}>
                          <img src={s.icon} alt="" />
                          <strong>{s.label}</strong>
                          <img src={WS.chrome.dashArrowOrange} alt="" className="ws-home__tile-arrow" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {!guest ? (
                  <div className="ws-home__ctas">
                    <button type="button" className="ws-btn-accent" onClick={() => onDeepLink?.('dp:deposit')}>{TR.home.depositCta}</button>
                    <button type="button" className="ws-btn-ghost" onClick={() => onDeepLink?.('dp:go&url=/tr/sport')}>{TR.home.playSport}</button>
                    <button type="button" className="ws-btn-ghost" onClick={() => onDeepLink?.('dp:go&url=/tr')}>{TR.home.playCasino}</button>
                  </div>
                ) : null}

                <div className="ws-home__section">
                  <div className="ws-home__section-head">
                    <h3>{TR.home.missionsTitle}</h3>
                    <button type="button" onClick={() => goTab(NAV.find((n) => n.id === 'missions'))}>{TR.home.seeAll}</button>
                  </div>
                  {homeMissions.length ? (
                    <ul className="ws-home__rail">
                      {homeMissions.map((m) => {
                        const pct = missionProgress(m);
                        const claim = showClaimOnCard(m);
                        return (
                          <li key={m.id} className={claim ? 'is-claimable' : ''} style={{ backgroundImage: `url(${WS.boxes.mission})` }}>
                            <button type="button" className="ws-home__rail-hit" onClick={() => { setTab('missions'); setDetail(m); }}>
                              <div className="ws-home__rail-art" style={{ backgroundImage: `url(${WS.boxes.content})` }}>
                                <img src={WS.home.missions} alt="" />
                                {claim ? <img src={WS.labels.hot} alt="" className="ws-home__rail-label" /> : null}
                              </div>
                              <strong>{m.name}</strong>
                              <em className={`ws-chip ws-chip--${missionBucket(m)}`}>{statusChip(m)}</em>
                              {pct > 0 ? <Progress value={pct} /> : (
                                <span className="ws-home__reward">
                                  <img src={WS.home.points} alt="" />
                                  {fmt(m.points_reward)}
                                </span>
                              )}
                            </button>
                            {claim ? (
                              <button
                                type="button"
                                className="ws-btn-claim"
                                disabled={busy === m.id}
                                onClick={(e) => { e.stopPropagation(); act(m.id, () => wsApi.requestMissionClaimReward(m.id)); }}
                              >
                                {TR.missionActions.claim}
                              </button>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <Empty src={WS.empty.missions} text={TR.home.emptyMissions} />
                  )}
                </div>

                <div className="ws-home__section">
                  <div className="ws-home__section-head">
                    <h3>{TR.home.boardTitle}</h3>
                    <button type="button" onClick={() => goTab(NAV.find((n) => n.id === 'board'))}>{TR.home.seeAll}</button>
                  </div>
                  {homeBoard.length ? (
                    <ul className="ws-home__board">
                      {homeBoard.map((row) => renderBoardRow({ ...row, _home: true }))}
                    </ul>
                  ) : (
                    <Empty src={WS.empty.default} text={TR.home.emptyBoard} />
                  )}
                </div>

                <div className="ws-home__section">
                  <div className="ws-home__section-head">
                    <h3>{TR.home.storeTitle}</h3>
                    <button type="button" onClick={() => goTab(NAV.find((n) => n.id === 'store'))}>{TR.home.seeAll}</button>
                  </div>
                  {homeStore.length ? (
                    <ul className="ws-home__store">
                      {homeStore.map((it, idx) => (
                        <li
                          key={it.id}
                          style={{ backgroundImage: `url(${WS.boxes.storeHome})` }}
                          onClick={() => (guest ? onRegister?.() : setBuyItem(it))}
                        >
                          {idx === 0 ? <img src={WS.labels.new} alt="" className="ws-store__label" /> : null}
                          {idx === 1 ? <img src={WS.labels.hot} alt="" className="ws-store__label" /> : null}
                          <img src={WS.nav.store} alt="" className="ws-home__store-ico" />
                          <strong>{it.name}</strong>
                          <em>
                            <img src={WS.home.points} alt="" />
                            {fmt(it.price ?? it.points_cost)}
                          </em>
                          <span className="ws-store__cta">{TR.store.buyNow}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Empty src={WS.empty.store} text={TR.home.emptyStore} />
                  )}
                </div>
              </div>
            )}

            {tab === 'missions' && (
              <>
                <div className="ws-gf__tabs">
                  {MISSION_TABS.map((t) => (
                    <button key={t.id} type="button" className={mTab === t.id ? 'is-active' : ''} onClick={() => setMTab(t.id)}>{t.label}</button>
                  ))}
                </div>
                {filteredMissions.length ? (
                  <ul className="ws-mission-grid">
                    {filteredMissions.map((m, idx) => {
                      const pct = missionProgress(m);
                      const bucket = missionBucket(m);
                      const requiresOpt = m.is_requires_optin && !m.is_opted_in && bucket !== 'locked' && bucket !== 'missed';
                      const showBar = pct > 0 || requiresOpt;
                      const showReward = pct === 0 && !requiresOpt && bucket !== 'completed';
                      const chip = statusChip(m);
                      const big = idx % 5 === 0 || idx % 5 === 3;
                      return (
                        <li
                          key={m.id}
                          className={[
                            'ws-mission',
                            `ws-mission--${bucket}`,
                            Number(focusId) === Number(m.id) ? 'is-focus' : '',
                            showClaimOnCard(m) ? 'is-claimable' : '',
                            big ? 'is-big' : '',
                          ].filter(Boolean).join(' ')}
                          style={{ backgroundImage: `url(${big ? WS.boxes.tall : WS.boxes.card})` }}
                        >
                          <button type="button" className="ws-mission__hit" onClick={() => setDetail(m)}>
                            <div className={`ws-mission__art${bucket === 'locked' ? ' is-locked' : ''}`} style={{ backgroundImage: `url(${WS.boxes.content})` }}>
                              <img src={WS.home.missions} alt="" />
                              {bucket === 'locked' ? <LockMark /> : null}
                              {showReward ? (
                                <span className="ws-mission__ribbon">
                                  <img src={WS.home.points} alt="" />
                                  {fmt(m.points_reward)}
                                </span>
                              ) : null}
                            </div>
                            <div className="ws-mission__copy">
                              <strong>{m.name}</strong>
                              <em className={`ws-chip ws-chip--${bucket}`}>{chip}</em>
                              <span className="ws-mission__desc">
                                {bucket === 'locked'
                                  ? (m.unlock_mission_description || m.description)
                                  : (m.description || m.tasks?.[0]?.name || '')}
                              </span>
                              {showBar ? <Progress value={requiresOpt && pct === 0 ? 0 : pct} /> : null}
                              {showBar ? <small>{TR.common.progress(pct)}</small> : null}
                            </div>
                          </button>
                          <div className="ws-mission__act">
                            {showClaimOnCard(m) ? (
                              <button
                                type="button"
                                className="ws-btn-claim"
                                disabled={busy === m.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  act(m.id, () => wsApi.requestMissionClaimReward(m.id));
                                }}
                              >
                                {busy === m.id ? '…' : TR.missionActions.claim}
                              </button>
                            ) : null}
                            {m.prize_claimed_date_ts ? <img src={WS.chrome.check} alt="" className="ws-check" /> : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <Empty src={WS.empty.missions} text={TR.missionEmpty[mTab] || TR.missionEmpty.overview} />
                )}
              </>
            )}

            {tab === 'levels' && (
              visibleLevels.length ? (
                <div className="ws-level-map">
                  <div className="ws-level-map__head">
                    <img src={WS.home.levels} alt="" />
                    <strong>{TR.levels.mapTitle}</strong>
                  </div>
                  <ol className="ws-levels">
                    {visibleLevels.map((lv, idx) => {
                      const current = profile?.level?.id === lv.id;
                      const reached = Number(profile?.ach_points_ever || 0) >= Number(lv.required_points);
                      const locked = !reached && !current && Number(profile?.ach_points_ever || 0) < Number(lv.required_points);
                      return (
                        <li
                          key={lv.id}
                          className={`ws-level${current ? ' is-current' : ''}${reached ? ' is-reached' : ''}${locked ? ' is-locked' : ''}`}
                          style={{ backgroundImage: `url(${WS.boxes.big})` }}
                          onClick={() => setLevelDetail(lv)}
                        >
                          <div className="ws-level__badge" style={{ backgroundImage: `url(${current ? WS.chrome.navBgActive : WS.chrome.navBg})` }}>
                            {reached ? <img src={WS.chrome.check} alt="" /> : <span>{idx + 1}</span>}
                          </div>
                          <div className="ws-level__copy">
                            <strong>{lv.name}</strong>
                            <span>{lv.description}</span>
                            {current ? <em className="ws-chip ws-chip--available">{TR.levels.current}</em> : null}
                            {locked ? <em className="ws-chip ws-chip--locked">{TR.levels.locked}</em> : null}
                          </div>
                          <em className="ws-level__pts">
                            <img src={WS.home.points} alt="" />
                            {fmt(lv.required_points)}
                          </em>
                          <img src={current ? WS.chrome.dashArrowOrange : WS.chrome.dashArrow} alt="" className="ws-level__arrow" />
                        </li>
                      );
                    })}
                  </ol>
                </div>
              ) : (
                <Empty src={WS.empty.default} text={TR.levels.empty} />
              )
            )}

            {tab === 'board' && (
              <>
                <div className="ws-gf__tabs">
                  {[['daily', TR.boardTabs.daily], ['weekly', TR.boardTabs.weekly], ['monthly', TR.boardTabs.monthly]].map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      className={lbType === id ? 'is-active' : ''}
                      onClick={() => { setLbType(id); setLbPrev(false); }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="ws-gf__tabs">
                  {[['leaders', TR.boardSections.leaders], ['prizes', TR.boardSections.prizes], ['rules', TR.boardSections.rules]].map(([id, label]) => (
                    <button key={id} type="button" className={lbSection === id ? 'is-active' : ''} onClick={() => setLbSection(id)}>{label}</button>
                  ))}
                </div>

                {lbSection === 'leaders' && (
                  boardRows.length ? (
                    <div className={`ws-board-wrap${lbPrev ? ' is-prev' : ''}`}>
                      <ol className="ws-board">
                        {boardRows.map(renderBoardRow)}
                      </ol>
                      {boardRows.length < 8 ? <div className="ws-board__pad" aria-hidden="true" /> : null}
                      {showStickyMe ? (
                        <div className="ws-board__me-sticky">
                          {me.position === -1 ? (
                            <p className="ws-board__unranked">{TR.board.unranked}</p>
                          ) : (
                            <ol className="ws-board">{renderBoardRow(me)}</ol>
                          )}
                        </div>
                      ) : null}
                      <button type="button" className="ws-board__prev-toggle" onClick={() => setLbPrev((v) => !v)}>
                        {prevToggleLabel(lbType, lbPrev)}
                      </button>
                    </div>
                  ) : (
                    <>
                      <Empty src={WS.empty.default} text={TR.board.empty} />
                      <button type="button" className="ws-board__prev-toggle" onClick={() => setLbPrev((v) => !v)}>
                        {prevToggleLabel(lbType, lbPrev)}
                      </button>
                    </>
                  )
                )}

                {lbSection === 'prizes' && (
                  <ul className="ws-prizes">
                    {(board?.rewards || []).map((r) => (
                      <li key={r.place} style={{ backgroundImage: `url(${WS.lb[r.place] || WS.lb.other})` }}>
                        <strong>{r.label_tr || TR.board.place(r.place)}</strong>
                        <span>
                          <img src={WS.home.points} alt="" />
                          {TR.board.prizePts(fmt(r.points))}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {lbSection === 'rules' && (
                  <div className="ws-rules" style={{ backgroundImage: `url(${WS.boxes.card})` }}>
                    <p>{board?.rules || ''}</p>
                  </div>
                )}
              </>
            )}

            {tab === 'store' && (
              <>
                <div className="ws-gf__tabs">
                  <button type="button" className={storeTab === 'items' ? 'is-active' : ''} onClick={() => setStoreTab('items')}>{TR.storeTabs.items}</button>
                  <button type="button" className={storeTab === 'history' ? 'is-active' : ''} onClick={() => setStoreTab('history')}>{TR.storeTabs.history}</button>
                </div>
                {storeTab === 'items' ? (
                  <>
                    <div className="ws-store-search" style={{ backgroundImage: `url(${WS.storeSearch})` }}>
                      <input
                        value={storeQ}
                        onChange={(e) => setStoreQ(e.target.value)}
                        placeholder={TR.store.search}
                        aria-label={TR.store.search}
                      />
                    </div>
                    <div className="ws-gf__tabs">
                      <button type="button" className={storeCat === 'all' ? 'is-active' : ''} onClick={() => setStoreCat('all')}>{TR.store.all}</button>
                      {cats.map((c) => (
                        <button key={c.id} type="button" className={Number(storeCat) === Number(c.id) ? 'is-active' : ''} onClick={() => setStoreCat(c.id)}>{c.name}</button>
                      ))}
                    </div>
                    {filteredStore.length ? (
                      <ul className="ws-store">
                        {filteredStore.map((it) => {
                          const price = Number(it.price ?? it.points_cost);
                          const canBuy = it.can_buy !== false;
                          const label = it.ribbon === 'new' ? WS.labels.new : (it.ribbon === 'hot' ? WS.labels.hot : null);
                          return (
                            <li
                              key={it.id}
                              className={`ws-store__card${!canBuy && !guest ? ' is-disabled' : ''}`}
                              style={{ backgroundImage: `url(${WS.boxes.store})` }}
                              onClick={() => (guest ? onRegister?.() : setBuyItem(it))}
                            >
                              {label ? <img src={label} alt="" className="ws-store__label" /> : null}
                              <div className="ws-store__art" style={{ backgroundImage: `url(${WS.boxes.content})` }}>
                                <img src={WS.nav.store} alt="" />
                              </div>
                              <strong>{it.name}</strong>
                              <div className="ws-store__foot">
                                <em>
                                  <img src={WS.home.points} alt="" />
                                  {fmt(price)}
                                </em>
                                <span className="ws-store__cta">{guest ? TR.guest.cta : TR.store.buyNow}</span>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <Empty src={WS.empty.store} text={storeQ ? TR.store.noSearch : TR.store.empty} />
                    )}
                  </>
                ) : (
                  storeHist.length ? (
                    <ul className="ws-gf__cards">
                      {storeHist.map((h) => (
                        <li key={h.id} className="ws-mission" style={{ backgroundImage: `url(${WS.boxes.card})` }}>
                          <div className="ws-mission__copy" style={{ padding: '8px 4px' }}>
                            <strong>{h.name}</strong>
                            <span>{fmt(h.price ?? h.points_spent)} {TR.common.points} · {h.purchase_ts ? new Date(h.purchase_ts).toLocaleString('tr-TR') : (h.created_at ? new Date(h.created_at).toLocaleString('tr-TR') : '')}</span>
                          </div>
                          {!guest ? (
                            <div className="ws-mission__act">
                              <button
                                type="button"
                                disabled={Number(profile?.ach_points_balance) < Number(h.points_cost ?? h.price)}
                                onClick={() => setBuyItem({
                                  id: h.item_id,
                                  name: h.name,
                                  description: h.description,
                                  price: h.price ?? h.points_cost,
                                  points_cost: h.points_cost,
                                })}
                              >
                                {TR.store.buyAgain}
                              </button>
                            </div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Empty src={WS.empty.store} text={TR.store.historyEmpty} />
                  )
                )}
              </>
            )}

            {tab === 'activity' && (
              <>
                <div className="ws-inbox-head" style={{ backgroundImage: `url(${WS.pointsHistoryHeader || WS.inbox.header})` }}>
                  <h2>{TR.activity.title}</h2>
                </div>
                <div className="ws-gf__tabs">
                  <button type="button" className={actFilter === 'all' ? 'is-active' : ''} onClick={() => setActFilter('all')}>{TR.activity.all}</button>
                  <button type="button" className={actFilter === 'today' ? 'is-active' : ''} onClick={() => setActFilter('today')}>{TR.activity.today}</button>
                  <button type="button" className={actFilter === 'week' ? 'is-active' : ''} onClick={() => setActFilter('week')}>{TR.activity.week}</button>
                </div>
                {filteredPoints.length ? (
                  <ul className="ws-gf__activity">
                    {filteredPoints.map((a) => (
                      <li key={a.id} style={{ backgroundImage: `url(${WS.boxes.card})` }}>
                        <div>
                          <strong>{reasonLabel(a.reason)}</strong>
                          <time>{a.created_at ? new Date(a.created_at).toLocaleString('tr-TR') : ''}</time>
                        </div>
                        <span className={`ws-delta${Number(a.amount) < 0 ? ' is-neg' : ''}`}>
                          {a.amount != null ? `${Number(a.amount) > 0 ? '+' : ''}${fmt(a.amount)}` : ''}
                          <img src={WS.home.points} alt="" />
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : activity.length && !pointsHist.length ? (
                  <ul className="ws-gf__activity">
                    {activity.map((a) => (
                      <li key={a.id} style={{ backgroundImage: `url(${WS.boxes.card})` }}>
                        <div>
                          <strong>{a.title_tr}</strong>
                          <time>{a.created_at ? new Date(a.created_at).toLocaleString('tr-TR') : ''}</time>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <Empty src={WS.empty.activity} text={TR.activity.pointsEmpty} />
                )}
              </>
            )}
          </div>
        </div>

        {!hideChrome && (
          <nav className="ws-gf__mob-nav" style={{ backgroundImage: `url(${WS.chrome.navbarMob})` }} aria-label="Mobil menü">
            {[NAV[0], NAV[1], NAV[3], NAV[4]].map((n) => (
              <button key={n.id} type="button" className={tab === n.id ? 'is-active' : ''} onClick={() => goTab(n)}>
                <img src={n.icon} alt="" />
                <span>{n.label}</span>
              </button>
            ))}
          </nav>
        )}
      </div>

      {toast ? <Toast text={toast} onDone={() => setToast('')} /> : null}

      {detail ? (
        <Popup onClose={() => setDetail(null)} wide>
          <div className="ws-mission-pop">
            <div className={`ws-mission-pop__art${missionBucket(detail) === 'locked' ? ' is-locked' : ''}`}>
              <img src={WS.home.missions} alt="" />
              {missionBucket(detail) === 'locked' ? <LockMark /> : null}
            </div>
            <em className={`ws-chip ws-chip--${missionBucket(detail)}`}>{statusChip(detail)}</em>
            <h3>{detail.name}</h3>
            {detail.sub_header ? <p className="ws-sub">{detail.sub_header}</p> : null}
            <p>{detail.description}</p>
            {(detail.tasks || []).length > 1 ? (
              <ul className="ws-tasks">
                {(detail.tasks || []).map((t) => {
                  const target = Number(t.execution_count_expected ?? t.target_value) || 1;
                  const cur = Number(t.execution_count_actual ?? t.current ?? 0);
                  const pct = Math.min(100, Math.round((cur / target) * 100));
                  const done = t.is_completed ?? cur >= target;
                  return (
                    <li key={t.id}>
                      <div>
                        <strong>
                          <span className={`ws-task-dot${done ? ' is-done' : ''}`} />
                          {t.name || t.label_tr || t.event_type}
                        </strong>
                        <span>{fmt(cur)} / {fmt(target)}</span>
                      </div>
                      <Progress value={pct} animate />
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {(missionProgress(detail) > 0 || (detail.is_requires_optin && !detail.is_opted_in)) ? (
              <Progress value={missionProgress(detail)} animate />
            ) : null}
            <div className="ws-reward-line">
              <img src={WS.home.points} alt="" />
              <strong>{fmt(detail.points_reward)} {TR.common.points}</strong>
            </div>
            {missionBucket(detail) === 'locked' && (detail.unlock_mission_description || detail.description) ? (
              <p className="ws-unlock">{detail.unlock_mission_description || detail.description}</p>
            ) : null}
            <div className="ws-gf__row">
              {detail.is_requires_optin && !detail.is_opted_in && missionBucket(detail) !== 'locked' && missionBucket(detail) !== 'missed' && !guest ? (
                <button type="button" disabled={busy === detail.id} onClick={() => act(detail.id, async () => { await wsApi.requestMissionOptIn(detail.id); setDetail(null); })}>
                  {busy === detail.id ? '…' : TR.missionActions.optIn}
                </button>
              ) : null}
              {isClaimable(detail) && !guest ? (
                <button type="button" className="ws-btn-claim" disabled={busy === detail.id} onClick={() => act(detail.id, async () => { await wsApi.requestMissionClaimReward(detail.id); setDetail(null); })}>
                  {busy === detail.id ? '…' : TR.missionActions.claim}
                </button>
              ) : null}
              {detail.cta_text && detail.cta_action && !detail.is_completed && !detail.prize_claimed_date_ts && missionBucket(detail) !== 'locked' ? (
                <button type="button" onClick={() => onDeepLink?.(detail.cta_action.startsWith('dp:') ? detail.cta_action : `dp:${detail.cta_action}`)}>
                  {detail.cta_text || TR.missionActions.ctaDefault}
                </button>
              ) : null}
            </div>
          </div>
        </Popup>
      ) : null}

      {levelDetail ? (
        <Popup onClose={() => setLevelDetail(null)}>
          <div className="ws-level-pop">
            <img src={WS.home.levels} alt="" className="ws-level-pop__ico" />
            <h3>{levelDetail.name}</h3>
            <p>{levelDetail.description}</p>
            <div className="ws-level-pop__req">
              <img src={WS.home.points} alt="" />
              <strong>{TR.levels.need(fmt(levelDetail.required_points))}</strong>
            </div>
            <Progress
              value={Math.min(100, Math.round((Number(profile?.ach_points_ever || 0) / Math.max(1, Number(levelDetail.required_points) || 1)) * 100))}
              animate
            />
            <small>{fmt(profile?.ach_points_ever)} / {fmt(levelDetail.required_points)}</small>
          </div>
        </Popup>
      ) : null}

      {nickOpen && !guest ? (
        <Popup onClose={() => setNickOpen(false)}>
          <h3>{TR.nick.title}</h3>
          <input value={nick} onChange={(e) => setNick(e.target.value)} maxLength={24} placeholder={profile?.public_username || profile?.nickname || TR.nick.placeholder} />
          <div className="ws-gf__row">
            <button type="button" className="ws-btn-ghost" onClick={() => setNickOpen(false)}>{TR.nick.cancel}</button>
            <button type="button" onClick={() => act('nick', async () => { await wsApi.setNickname(nick); setNickOpen(false); })}>{TR.nick.save}</button>
          </div>
        </Popup>
      ) : null}

      {buyItem && !guest ? (
        <Popup onClose={() => setBuyItem(null)}>
          <img src={WS.nav.store} alt="" className="ws-buy-art" />
          <h3>{buyItem.name}</h3>
          <p>{buyItem.description}</p>
          <p className="ws-price-line">
            <img src={WS.home.points} alt="" className="ws-inline-ico" />
            {fmt(buyItem.price ?? buyItem.points_cost)} {TR.common.points}
          </p>
          {buyItem.can_buy === false || Number(profile?.ach_points_balance) < Number(buyItem.price ?? buyItem.points_cost) ? (
            <p className="ws-short">{TR.store.shortBy(fmt(Math.max(0, Number(buyItem.price ?? buyItem.points_cost) - Number(profile?.ach_points_balance || 0))))}</p>
          ) : null}
          <div className="ws-gf__row">
            <button type="button" className="ws-btn-ghost" onClick={() => setBuyItem(null)}>{TR.store.cancel}</button>
            <button
              type="button"
              disabled={busy === `s${buyItem.id}` || buyItem.can_buy === false || Number(profile?.ach_points_balance) < Number(buyItem.price ?? buyItem.points_cost)}
              onClick={() => act(`s${buyItem.id}`, async () => { await wsApi.buyStoreItem(buyItem.id); setBuyItem(null); }, { successToast: TR.store.success })}
            >
              {busy === `s${buyItem.id}` ? '…' : TR.store.confirm}
            </button>
          </div>
        </Popup>
      ) : null}
    </div>
  );

  if (typeof document === 'undefined') return ui;
  return createPortal(ui, document.body);
}
